/**
 * @fileoverview Margin unit (cm/px) utilities for the document margin editors.
 * @module utils/branding/marginUnits
 *
 * Purpose:
 * - Centralise the px <-> cm conversion math used by the branding settings
 *   margin card and the editor toolbar margin popover.
 * - Provide a small `useMarginUnit` hook that persists the user's preferred
 *   display unit in `localStorage` and keeps every mounted instance in sync
 *   (cross-tab via the native `storage` event, intra-tab via a tiny pub-sub).
 *
 * Storage stays in pixels (Convex schema unchanged); these helpers only
 * affect what the user sees and types.
 */

import { useCallback, useEffect, useState } from "react";

export type MarginUnit = "cm" | "px";

export const MARGIN_UNIT_STORAGE_KEY = "morgan.marginUnit";

/** CSS px per inch at 96 DPI - matches the existing Word exporter. */
const PX_PER_INCH = 96;
/** Centimetres per inch. */
const CM_PER_INCH = 2.54;

/** Display caps shared between both editors. */
export const MARGIN_LIMITS = {
  cm: { min: 0, max: 10 },
  /** ~10.6 cm — kept slightly above the cm cap so the units stay equivalent. */
  px: { min: 0, max: 400 },
} as const satisfies Record<MarginUnit, { min: number; max: number }>;

/**
 * Convert centimetres to integer CSS pixels (96 DPI).
 *
 * Storage is integer px, so we round here to avoid drifting on round-trip.
 */
export function cmToPx(cm: number): number {
  return Math.round((cm * PX_PER_INCH) / CM_PER_INCH);
}

/** Convert integer CSS pixels to centimetres (96 DPI). */
export function pxToCm(px: number): number {
  return (px * CM_PER_INCH) / PX_PER_INCH;
}

/**
 * Format a stored px value for display in the given unit. Used to seed input
 * drafts so the user always sees their saved value in the active unit.
 *
 * cm values are shown with up to 2 decimal places (trailing zeros trimmed)
 * to match how Word's Page Setup dialog renders margins (e.g. `2.54`, `1.5`).
 */
export function formatForUnit(px: number, unit: MarginUnit): string {
  if (unit === "px") return String(Math.round(px));
  const cm = pxToCm(px);
  const rounded = Math.round(cm * 100) / 100;
  return rounded
    .toFixed(2)
    .replace(/\.?0+$/, "")
    .replace(/^$/, "0");
}

export type ParseResult =
  | { kind: "empty" }
  | { kind: "invalid"; reason: string }
  | { kind: "out_of_range"; reason: string }
  | { kind: "ok"; px: number };

/**
 * Parse a raw input string in the active unit, validate it against
 * `MARGIN_LIMITS`, and return the equivalent integer px on success.
 *
 * Callers decide what to do for each result kind (e.g. ignore `empty` vs
 * surface a toast for `invalid` / `out_of_range`).
 */
export function parseInForUnit(raw: string, unit: MarginUnit): ParseResult {
  const trimmed = raw.trim();
  if (trimmed === "") return { kind: "empty" };

  const value = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(value)) {
    return {
      kind: "invalid",
      reason: `Enter a number in ${unit}.`,
    };
  }

  const { min, max } = MARGIN_LIMITS[unit];
  if (value < min || value > max) {
    return {
      kind: "out_of_range",
      reason: `Margin must be between ${min} and ${max} ${unit}.`,
    };
  }

  return {
    kind: "ok",
    px: unit === "px" ? Math.round(value) : cmToPx(value),
  };
}

// ---------------------------------------------------------------------------
// useMarginUnit hook — shared preference across all margin editors
// ---------------------------------------------------------------------------

type Listener = (unit: MarginUnit) => void;
const listeners = new Set<Listener>();

function readStoredUnit(): MarginUnit {
  if (typeof window === "undefined") return "cm";
  try {
    const raw = window.localStorage.getItem(MARGIN_UNIT_STORAGE_KEY);
    return raw === "px" ? "px" : "cm";
  } catch {
    return "cm";
  }
}

function writeStoredUnit(unit: MarginUnit) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MARGIN_UNIT_STORAGE_KEY, unit);
  } catch {
    // localStorage may be unavailable (private mode, quota); ignore.
  }
}

/**
 * Read/write the user's preferred margin unit. Defaults to `"cm"` per
 * MOR-211. Setting the unit broadcasts to every other mounted consumer so
 * toggling on the branding page also flips the editor popover (and vice
 * versa) without remounting.
 */
export function useMarginUnit(): [MarginUnit, (next: MarginUnit) => void] {
  const [unit, setUnit] = useState<MarginUnit>(() => readStoredUnit());

  useEffect(() => {
    const listener: Listener = (next) => setUnit(next);
    listeners.add(listener);

    const onStorage = (e: StorageEvent) => {
      if (e.key !== MARGIN_UNIT_STORAGE_KEY) return;
      setUnit(e.newValue === "px" ? "px" : "cm");
    };
    window.addEventListener("storage", onStorage);

    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const update = useCallback((next: MarginUnit) => {
    writeStoredUnit(next);
    setUnit(next);
    listeners.forEach((l) => l(next));
  }, []);

  return [unit, update];
}
